import { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import { catalogFetch } from "./catalogApi.js";

function MarketOrdersTable({
  disabled = false,
  authToken = "",
  onTakeOpposite,
  selectedMarketId: externalSelectedMarketId = "",
  selectedMarketIds = [],
  availableMarkets = null,
  showMarketSelector = true,
}) {
  const [internalMarkets, setInternalMarkets] = useState([]);
  const [currentMarketId, setCurrentMarketId] = useState("");
  const [orders, setOrders] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState("");
  const markets = Array.isArray(availableMarkets) ? availableMarkets : internalMarkets;

  useEffect(() => {
    setCurrentMarketId(externalSelectedMarketId || "");
  }, [externalSelectedMarketId]);

  useEffect(() => {
    if (Array.isArray(availableMarkets) || !showMarketSelector) {
      setLoadingMarkets(false);
      return undefined;
    }

    let cancelled = false;

    const loadMarkets = async () => {
      if (!authToken) {
        setError("");
        setInternalMarkets([]);
        return;
      }

      setLoadingMarkets(true);
      setError("");
      try {
        const body = await catalogFetch("/markets", authToken);
        const items = Array.isArray(body.items) ? body.items : [];
        const validItems = items.filter((item) => item && item.market_id);
        if (!cancelled) {
          setInternalMarkets(validItems);
          setCurrentMarketId((current) => {
            if (current && validItems.some((item) => item.market_id === current)) {
              return current;
            }
            return validItems[0]?.market_id || "";
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load markets");
        }
      } finally {
        if (!cancelled) {
          setLoadingMarkets(false);
        }
      }
    };

    loadMarkets();
    return () => {
      cancelled = true;
    };
  }, [availableMarkets, showMarketSelector, authToken]);

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      const marketIds = selectedMarketIds.length > 0
        ? selectedMarketIds
        : (currentMarketId ? [currentMarketId] : []);
      if (marketIds.length === 0 || !authToken) {
        setOrders([]);
        return;
      }

      setLoadingOrders(true);
      setError("");
      try {
        const responses = await Promise.all(
          marketIds.map(async (marketId) => {
            const body = await catalogFetch(
              `/markets/${encodeURIComponent(marketId)}/orders`,
              authToken
            );
            return Array.isArray(body.items) ? body.items : [];
          })
        );
        if (!cancelled) {
          setOrders(responses.flat());
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load market orders");
          setOrders([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingOrders(false);
        }
      }
    };

    loadOrders();
    return () => {
      cancelled = true;
    };
  }, [currentMarketId, selectedMarketIds, authToken]);

  const marketLabelById = useMemo(() => {
    const labels = {};
    markets.forEach((market) => {
      if (!market?.market_id) {
        return;
      }
      labels[market.market_id] = market.option_label || market.title || market.market_id;
    });
    return labels;
  }, [markets]);

  const canTakeOpposite = (order) =>
    typeof onTakeOpposite === "function"
    && Number(order.remaining_qty) > 0
    && order.status !== "FILLED";

  return (
    <div className="workspace-table-shell">
      {error && <Alert variant="warning">{error}</Alert>}
      <div className="workspace-table-header">
        <div>
          <div className="workspace-table-title">Order Book</div>
          <div className="workspace-table-subtitle">All submitted orders for the selected market view.</div>
        </div>
      </div>
      <div className="d-flex gap-2 align-items-end mb-3 workspace-table-toolbar">
        {showMarketSelector && (
          <Form.Group className="flex-grow-1 m-0 workspace-table-selector">
            <Form.Label>Market</Form.Label>
            <Form.Select
              value={currentMarketId}
              onChange={(e) => setCurrentMarketId(e.target.value)}
              disabled={disabled || loadingMarkets || markets.length === 0}
            >
              {markets.length === 0 ? (
                <option value="">
                  {loadingMarkets ? "Loading markets..." : "No markets available"}
                </option>
              ) : (
                markets.map((market) => (
                  <option key={market.market_id} value={market.market_id}>
                    {market.title || market.market_id}
                  </option>
                ))
              )}
            </Form.Select>
          </Form.Group>
        )}
      </div>

      <div className="table-scroll-region">
        <Table striped bordered hover responsive variant="dark" className="workspace-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Market</th>
              <th>User</th>
              <th>Outcome</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Remaining</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr className="workspace-table-empty-row">
                <td colSpan={9}>
                  {loadingOrders ? "Loading orders..." : "No orders for this category"}
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.order_id}>
                  <td>{order.order_id}</td>
                  <td>{marketLabelById[order.market_id] || order.market_id || "-"}</td>
                  <td>{order.username || order.user_id || "-"}</td>
                  <td>{order.outcome || "-"}</td>
                  <td>{order.limit_price ?? "-"}</td>
                  <td>{order.qty ?? "-"}</td>
                  <td>{order.remaining_qty ?? "-"}</td>
                  <td>{order.status || "-"}</td>
                  <td>
                    {canTakeOpposite(order) ? (
                      <Button
                        size="sm"
                        className="workspace-table-action"
                        variant="outline-primary"
                        disabled={disabled}
                        onClick={() => onTakeOpposite(order)}
                      >
                        Take Opposite
                      </Button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default MarketOrdersTable;
